import { LocalizationService } from '@abp/ng.core';
import { Component, EventEmitter, Output } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { take } from 'rxjs';
import { DateDisplayType } from 'src/ca-shared/conversation-date-filter/models/date-display-type.enum';
import { LLMConfigurationDto } from 'src/core/models/llm/llm-configuration.dto';
import { MatTableColumnDefinitionModel } from 'src/core/models/mat-table/mat-table-column-definition.model';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';
import { SorterItemDto } from 'src/core/models/request/sorter-item.dto';
import { CADatePipe } from 'src/core/pipes/ca-date.pipe';
import { CrudService } from 'src/core/services/crud/crud.service';

@Component({
    selector: 'ca-llm-configuration-list',
    templateUrl: './llm-configuration-list.component.html',
    styleUrl: './llm-configuration-list.component.scss',
    standalone: false
})
export class LLMConfigurationListComponent {
  @Output()
  showDetails: EventEmitter<{
    id: number;
  }> = new EventEmitter();

  @Output()
  listLoadRequestCompleted = new EventEmitter();

  dataSource = new MatTableDataSource<LLMConfigurationDto>();
  data: LLMConfigurationDto[] = [];
  displayedColumns: string[] = [];
  totalCount = 0;
  skipCount = 0;
  pageSize = 10;
  currentPage = 0;

  filters: FilterItemDto[] = [];
  sorters: SorterItemDto[] = [];
  sortField = 'lastModificationDate';
  sortDirection = 'asc';

  gridColumns: MatTableColumnDefinitionModel[] = [
    {
      columnName: 'name',
      header: this.localizationService.instant('::Name'),
      binding: 'name',
      valueParser: undefined,
      pipes: undefined,
      width: '30%',
      tooltip: undefined,
    },
    {
      columnName: 'llmProviderName',
      header: this.localizationService.instant('Settings::LLMConfiguration:Provider'),
      binding: 'llmProviderName',
      valueParser: undefined,
      pipes: undefined,
      width: '30%',
      tooltip: undefined,
    },
    {
      columnName: 'lastModificationTime',
      header: this.localizationService.instant('::ModificationTime'),
      binding: 'lastModificationTime',
      valueParser: val => {
        if (val === null) {
          return null;
        }

        return new Date(val);
      },
      pipes: [
        {
          pipe: CADatePipe,
          args: [DateDisplayType.DateTimeWithoutSeconds, true],
        },
      ],
      width: '20%',
      tooltip: this.localizationService.instant('::ModificationTime'),
    },
    {
      columnName: 'creationTime',
      header: this.localizationService.instant('::CreationTime'),
      binding: 'creationTime',
      valueParser: val => {
        return new Date(val);
      },
      pipes: [
        {
          pipe: CADatePipe,
          args: [DateDisplayType.DateTimeWithoutSeconds, true],
        },
      ],
      width: '20%',
      tooltip: undefined,
    },
  ];

  onRowDoubleClick(eventArgs, llmConfigurationId) {
    this.openDetails(llmConfigurationId);
  }

  openDetails(llmConfigurationId) {
    this.showDetails.emit({ id: llmConfigurationId });
  }

  load(filters: FilterItemDto[], sorters: SorterItemDto[], filtered: boolean = false) {
    if (filtered === true) {
      this.currentPage = 0;
    }
    this.filters = filters;
    this.sorters = sorters;

    if (this.sortField && this.sortDirection) {
      this.sorters.push({
        field: this.sortField,
        direction: this.sortDirection.toUpperCase(),
      });
    }

    this.service
      .get<LLMConfigurationDto>(LLMConfigurationDto, {
        filters,
        sorters,
        maxResultCount: this.pageSize,
        skipCount: this.pageSize * this.currentPage,
      })
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.data = response.items;
          this.totalCount = response.totalCount;
          this.dataSource.connect().next(this.data);
          this.listLoadRequestCompleted.emit();
        },
        error: () => {
          this.listLoadRequestCompleted.emit();
        },
      });
  }

  refresh(): void {
    this.currentPage = 0;
    this.load(this.filters, this.sorters);
  }

  sortData(eventArgs: Sort) {
    this.sortDirection = eventArgs.direction;
    this.sortField = eventArgs.active;
    this.refresh();
  }

  onPageChange(event?: PageEvent) {
    this.currentPage = event.pageIndex;
    this.load(this.filters, this.sorters);
  }

  ngOnInit() {}

  constructor(private service: CrudService, private localizationService: LocalizationService) {
    for (const gridColumn of this.gridColumns) {
      this.displayedColumns.push(gridColumn.columnName);
    }
  }
}
